import * as React from 'react';
import useAuth from '@sdk/authentication/useAuth';
import Picker from '@sdk/components/forms/Picker';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import gettext from '@sdk/lang/gettext';
import { WEEKDAYS_OPTIONS } from '@sdk/common/constants';
import { Feature } from '@sdk/common/features';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import HoursOfOperation from '../HoursOfOperation';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import getPremiumTooltipId from '../../../premiumGuard/getPremiumTooltipId';
import PremiumGuard from '../../../premiumGuard/PremiumGuard';
import usePremiumGuard from '../../../premiumGuard/usePremiumGuard';
import { PremiumModalNames } from '../../../premiumGuard/constants/modalNames';
import { FIELD_WIDTH_205 } from '../../constants';
import { VisibilitySchedulesOptions } from '../../types';
import {
  getUsersSeeOtherSchedulesOption,
  getUsersSeeOtherSchedulesOptions,
  setUsersSeeOtherSchedulesOption,
} from '../../helpers/utils';
import { useUserOrgSettings } from '../../UserOrgSettingsContext';

type Props = {
  orgLocationsCount: number;
};

const ScheduleSettings: React.FC<React.PropsWithChildren<Props>> = ({ orgLocationsCount }) => {
  const { currentUser } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();

  return (
    <>
      <SettingsSection withSeparator>
        <Column>
          <RowSpaceBetween>
            <Column>
              <Text14 semiBold themeColor={ThemeColorType.gray}>
                {gettext('First day of the week')}
              </Text14>
              <Text14 themeColor={ThemeColorType.gray}>
                {gettext('Affects your schedule layout and labor cost calculation.')}
              </Text14>
            </Column>

            <Picker
              disabled={!currentUser.isAdmin}
              options={WEEKDAYS_OPTIONS}
              value={userOrgSettings.firstDayOfWeek}
              customWidth={FIELD_WIDTH_205}
              onChange={(value: number): void => onUserOrgSettingsChange({ firstDayOfWeek: value })}
            />
          </RowSpaceBetween>
        </Column>
      </SettingsSection>

      <HoursOfOperation
        visibleHoursStart={userOrgSettings.visibleHoursStart}
        visibleHoursEnd={userOrgSettings.visibleHoursEnd}
        enableShiftCostSplitting={userOrgSettings.enableShiftCostSplitting}
        onUserOrgSettingsChange={onUserOrgSettingsChange}
        usTimeFormat={currentUser.settings.usTimeFormat}
        laborCostCalculatorType={userOrgSettings.laborCostCalculatorType}
      />

      <SettingsSection withSeparator>
        <CollapsibleSettingsSection
          isActive={userOrgSettings.usersCanSeeOtherSchedules}
          headerItem={
            <SettingsSwitcherRow
              disabled={!currentUser.isAdmin}
              title={gettext('Visibility of schedules for employees')}
              subtitle={gettext("Allow employees to view other employees' schedules.")}
              value={userOrgSettings.usersCanSeeOtherSchedules}
              onChange={(value): void =>
                onUserOrgSettingsChange({ usersCanSeeOtherSchedules: value })
              }
            />
          }
          contentItem={
            <Column pTop={20}>
              <Picker
                disabled={!currentUser.isAdmin}
                options={getUsersSeeOtherSchedulesOptions(
                  orgLocationsCount,
                  userOrgSettings.usersCanSeeUsersOutsideLocation
                )}
                value={getUsersSeeOtherSchedulesOption({
                  orgLocationsCount,
                  canSeeOtherLocations: userOrgSettings.usersCanSeeSchedulesInOtherLocations,
                  canSeeOtherPositions: userOrgSettings.usersCanSeeSchedulesForOtherPositions,
                })}
                onChange={(value: VisibilitySchedulesOptions): void =>
                  setUsersSeeOtherSchedulesOption(value, onUserOrgSettingsChange)
                }
              />
              <SettingsSwitcherRow
                pTop={20}
                value={userOrgSettings.usersCanSeeCoworkersForSchedule}
                disabled={!currentUser.isAdmin}
                onChange={(value): void =>
                  onUserOrgSettingsChange({ usersCanSeeCoworkersForSchedule: value })
                }
                title={gettext('Visibility of coworkers for employees')}
                subtitle={gettext(
                  "Allow employees to check who they're working with on specific shifts."
                )}
              />
            </Column>
          }
        />
      </SettingsSection>

      <SettingsSection withSeparator>
        <SettingsSwitcherRow
          disabled={!currentUser.isAdmin}
          value={userOrgSettings.usersCanSeeTimeOffForOtherUsers}
          onChange={(value): void =>
            onUserOrgSettingsChange({ usersCanSeeTimeOffForOtherUsers: value })
          }
          title={gettext('Visibility of time off and unavailability')}
          subtitle={gettext(
            'Allow employees to view other employees’ time off and unavailability.'
          )}
        />
      </SettingsSection>

      <PremiumGuard
        premiumGuardModalName={PremiumModalNames.managerScheduleVisibility}
        hasAccess={currentUser.org.isPremium}
        tooltipRef={
          tooltipRefs[getPremiumTooltipId(Feature.managersCanSeeSchedulesInOtherLocations)]
        }>
        <SettingsSwitcherRow
          pBottom={20}
          disabled={!currentUser.isAdmin}
          value={userOrgSettings.managersCanSeeSchedulesInOtherLocations}
          onChange={(value): void =>
            onUserOrgSettingsChange({ managersCanSeeSchedulesInOtherLocations: value })
          }
          title={gettext('Visibility of schedules for managers')}
          subtitle={gettext("Allow managers to view other locations' schedules.")}
          premiumGuard={getPremiumGuardConfig(Feature.managersCanSeeSchedulesInOtherLocations)}
        />
      </PremiumGuard>
    </>
  );
};

export default ScheduleSettings;
